import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ActionPlanWithOwner, ActionPlanStatus, ApiResponse } from '@/types'

// Fetch action plans for a goal (with archive filter)
export function useActionPlans(goalId: string | undefined, archived = false) {
  return useQuery({
    queryKey: ['action-plans', { goalId, archived }],
    queryFn: async (): Promise<ActionPlanWithOwner[]> => {
      const res = await fetch(`/api/action-plans?goal_id=${goalId}&archived=${archived}`)
      const data: ApiResponse<ActionPlanWithOwner[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch action plans')
      return data.data!
    },
    enabled: !!goalId,
  })
}

// Fetch single action plan
export function useActionPlan(id: string | undefined) {
  return useQuery({
    queryKey: ['action-plans', id],
    queryFn: async (): Promise<ActionPlanWithOwner> => {
      const res = await fetch(`/api/action-plans/${id}`)
      const data: ApiResponse<ActionPlanWithOwner> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch action plan')
      return data.data!
    },
    enabled: !!id,
  })
}

// Create action plan (owner inherits from goal automatically)
export function useCreateActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (plan: {
      goal_id: string
      title: string
      description?: string
      expected_outcome?: string
      status?: 'draft' | 'active'
      start_date?: number
      target_date?: number
    }) => {
      const res = await fetch('/api/action-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan),
      })
      const data: ApiResponse<{ id: string }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create action plan')
      return data.data!
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goal_id }] })
    },
  })
}

// Update action plan
export function useUpdateActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...rest }: {
      id: string
      goalId: string // for cache invalidation
      title?: string
      description?: string
      expected_outcome?: string
      status?: ActionPlanStatus
      start_date?: number | null
      target_date?: number | null
    }) => {
      const plan: Partial<typeof rest> = { ...rest }
      delete plan.goalId // cache key only, not part of the request body
      const res = await fetch(`/api/action-plans/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(plan),
      })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update action plan')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goalId }] })
      queryClient.invalidateQueries({ queryKey: ['action-plans', variables.id] })
    },
  })
}

// Delete action plan
export function useDeleteActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; goalId: string }) => {
      const res = await fetch(`/api/action-plans/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete action plan')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goalId }] })
    },
  })
}

// Mark as completed
export function useCompleteActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; goalId: string }) => {
      const res = await fetch(`/api/action-plans/${id}/complete`, { method: 'POST' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to complete action plan')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goalId }] })
    },
  })
}

// Archive action plan
export function useArchiveActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; goalId: string }) => {
      const res = await fetch(`/api/action-plans/${id}/archive`, { method: 'POST' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to archive action plan')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goalId }] })
    },
  })
}

// Unarchive action plan
export function useUnarchiveActionPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: string; goalId: string }) => {
      const res = await fetch(`/api/action-plans/${id}/unarchive`, { method: 'POST' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to unarchive action plan')
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-plans', { goalId: variables.goalId }] })
    },
  })
}
